import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Search, MessageCircle, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { getAuthHeader } from '../../services/otpAuthService';
import SearchBar from './components/SearchBar';

const API_BASE_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace('/graphql', '')
  : 'http://localhost:5000';

interface SearchResult {
  _id: string;
  title: string;
  content: string;
  area?: { _id: string; displayName: string; slug: string; color?: string };
  author?: { businessName: string };
  repliesCount: number;
  viewsCount: number;
  createdAt: string;
}

const timeAgo = (date: string) => {
  const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (seconds < 60) return 'adesso';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m fa`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h fa`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}g fa`;
  return new Date(date).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
};

const stripHtml = (html: string) => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

const getSnippet = (text: string, query: string, maxLen = 200): string => {
  if (!query.trim() || text.length <= maxLen) return text.substring(0, maxLen);
  const words = query.trim().split(/\s+/).filter(w => w.length >= 2);
  if (words.length === 0) return text.substring(0, maxLen);
  const escaped = words.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const regex = new RegExp(escaped.join('|'), 'i');
  const match = regex.exec(text);
  if (!match) return text.substring(0, maxLen);
  const pos = match.index;
  const start = Math.max(0, pos - Math.floor(maxLen / 3));
  const end = Math.min(text.length, start + maxLen);
  const snippet = text.substring(start, end);
  return (start > 0 ? '...' : '') + snippet + (end < text.length ? '...' : '');
};

const highlightText = (text: string, query: string): React.ReactNode => {
  if (!query.trim()) return text;
  const words = query.trim().split(/\s+/).filter(w => w.length >= 2);
  if (words.length === 0) return text;
  const escaped = words.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const regex = new RegExp(`(${escaped.join('|')})`, 'gi');
  const parts = text.split(regex);
  return parts.map((part, i) =>
    regex.test(part)
      ? <mark key={i} className="bg-yellow-200 dark:bg-yellow-700/60 text-inherit rounded-sm px-0.5">{part}</mark>
      : part
  );
};

const SearchResultsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const query = searchParams.get('q') || '';
  const pageParam = parseInt(searchParams.get('page') || '1', 10);

  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 0 });

  const fetchResults = useCallback(async (q: string, page: number) => {
    if (!q || q.trim().length < 2) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ q: q.trim(), page: String(page), limit: '20' });
      const res = await fetch(
        `${API_BASE_URL}/api/community/v2/search?${params}`,
        { headers: getAuthHeader() as any }
      );
      if (res.ok) {
        const data = await res.json();
        setResults(data.data || []);
        setPagination(data.pagination || { page: 1, total: 0, pages: 0 });
      } else {
        toast.error('Errore nella ricerca');
      }
    } catch {
      toast.error('Errore nella ricerca');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
  }, []);

  useEffect(() => {
    fetchResults(query, pageParam);
  }, [query, pageParam, fetchResults]);

  const goToPage = (p: number) => {
    const params = new URLSearchParams({ q: query, page: String(p) });
    navigate(`/area-riservata/community-v2/cerca?${params}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3 mb-3">
            <button
              onClick={() => navigate(-1)}
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            </button>
            <h1 className="text-base font-semibold text-gray-900 dark:text-white">Risultati ricerca</h1>
          </div>
          <SearchBar className="w-full" initialQuery={query} />
        </div>
      </div>

      {/* Results */}
      <div className="max-w-4xl mx-auto px-4 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            <span className="text-sm">Ricerca in corso...</span>
          </div>
        ) : !query || query.length < 2 ? (
          <div className="text-center py-16">
            <Search className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-sm text-gray-500">Inserisci almeno 2 caratteri per cercare</p>
          </div>
        ) : results.length === 0 ? (
          <div className="text-center py-16">
            <Search className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-sm text-gray-500">
              Nessun risultato per "<span className="font-medium text-gray-700 dark:text-gray-300">{query}</span>"
            </p>
          </div>
        ) : (
          <>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              {pagination.total} risultat{pagination.total === 1 ? 'o' : 'i'} per "<span className="font-medium">{query}</span>"
            </p>

            <div className="space-y-2">
              {results.map((r) => (
                <Link
                  key={r._id}
                  to={`/area-riservata/community-v2/discussione/${r._id}?hl=${encodeURIComponent(query)}`}
                  className="block bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:border-indigo-300 dark:hover:border-indigo-600 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-2">
                        {highlightText(r.title, query)}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mt-1">
                        {highlightText(getSnippet(stripHtml(r.content), query), query)}
                      </p>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        {r.area && (
                          <span
                            className="inline-block text-xs font-medium px-2 py-0.5 rounded-full"
                            style={r.area.color ? { backgroundColor: r.area.color + '22', color: r.area.color } : undefined}
                          >
                            {r.area.displayName}
                          </span>
                        )}
                        {r.author && (
                          <span className="text-xs text-gray-400">{r.author.businessName}</span>
                        )}
                        <span className="text-xs text-gray-400 flex items-center gap-0.5">
                          <MessageCircle className="w-3 h-3" /> {r.repliesCount}
                        </span>
                        <span className="text-xs text-gray-400">{timeAgo(r.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <button
                  onClick={() => goToPage(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-xs text-gray-600 dark:text-gray-400">
                  Pagina {pagination.page} di {pagination.pages}
                </span>
                <button
                  onClick={() => goToPage(pagination.page + 1)}
                  disabled={pagination.page >= pagination.pages}
                  className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SearchResultsPage;
